import { useTranslation } from 'react-i18next'
import { Link, useLoaderData } from 'react-router'
import { HeroBackdrop } from '#app/components/hero-backdrop.tsx'
import { ImageFallback } from '#app/components/image-fallback.tsx'
import { badgeVariants } from '#app/components/ui/badge.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { CardWave } from '#app/components/ui/card-wave.tsx'
import { Card } from '#app/components/ui/card.tsx'
import { EmptyState } from '#app/components/ui/empty-state.tsx'
import { glassControlClassName } from '#app/components/ui/glass.ts'
import { Icon } from '#app/components/ui/icon.tsx'
import { Reveal } from '#app/components/ui/reveal.tsx'
import { WordReveal } from '#app/components/ui/word-reveal.tsx'
import {
	getLocalizedField,
	type SupportedLocale,
} from '#app/utils/blog-i18n.ts'
import { generateBlogListJsonLd } from '#app/utils/blog-seo.ts'
import { getBlogCategories, getPublishedPosts } from '#app/utils/blog.server.ts'
import { localizedPath, useLocalizedPath, useLang } from '#app/utils/i18n.ts'
import { cn, getDomainUrl, getBlogImgSrc } from '#app/utils/misc.tsx'
import {
	BlogPostCard,
	type PostWithRelations,
} from './+blog-components/blog-post-card.tsx'
import { BlogSearchForm } from './+blog-components/blog-search-form.tsx'
import { Pagination } from './+blog-components/pagination.tsx'
import { type Route } from './+types/blog._index.ts'

const PAGE_SIZE = 12

const BLOG_DESCRIPTION =
	'Einblicke, Tutorials und Neuigkeiten rund um Open-Source-Softwareentwicklung.'

export const meta: Route.MetaFunction = ({ data, params }) => {
	if (!data) return []

	const locale = (params.lang || 'de') as SupportedLocale
	const blogUrl = `${data.siteUrl}${localizedPath(locale, '/blog')}`

	const meta: Array<Record<string, unknown>> = [
		{ title: 'Blog | Open Sourced' },
		{ name: 'description', content: BLOG_DESCRIPTION },
		// Open Graph
		{ property: 'og:title', content: 'Blog | Open Sourced' },
		{ property: 'og:description', content: BLOG_DESCRIPTION },
		{ property: 'og:type', content: 'website' },
		{ property: 'og:url', content: blogUrl },
	]

	// JSON-LD structured data
	const jsonLd = generateBlogListJsonLd({
		siteUrl: data.siteUrl,
		locale,
		description: BLOG_DESCRIPTION,
	})
	meta.push({ 'script:ld+json': jsonLd })

	return meta
}

export async function loader({ request }: Route.LoaderArgs) {
	const url = new URL(request.url)
	const page = Math.max(1, Number(url.searchParams.get('page')) || 1)
	const siteUrl = (process.env.APP_URL || getDomainUrl(request)).replace(
		/\/$/,
		'',
	)

	const [{ posts, totalCount, totalPages, currentPage }, categories] =
		await Promise.all([
			getPublishedPosts({ page, pageSize: PAGE_SIZE }),
			getBlogCategories(),
		])

	return { posts, totalCount, totalPages, currentPage, categories, siteUrl }
}

function FeaturedPost({
	post,
	lang,
}: {
	post: PostWithRelations
	lang: string
}) {
	const { t } = useTranslation()
	const localePath = useLocalizedPath()
	const locale = lang as SupportedLocale
	const title = getLocalizedField(post, 'title', locale)
	const excerpt = getLocalizedField(post, 'excerpt', locale)

	// The title link stretches over the whole card, so the card lifts as one
	// link. The category link sits above that overlay and stays its own target.
	return (
		<Card
			asChild
			interactive
			className="group relative gap-0 overflow-hidden p-0 md:grid md:grid-cols-2"
		>
			<article>
				{/* Hero Image */}
				<div className="aspect-video overflow-hidden md:aspect-auto md:h-full md:min-h-48">
					{post.heroImage ? (
						<img
							src={getBlogImgSrc(post.heroImage.objectKey)}
							alt={post.heroImage.altText || title}
							className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-105"
						/>
					) : (
						<ImageFallback label={title} />
					)}
				</div>

				{/* Content */}
				<div className="flex flex-col justify-center p-6 md:p-8">
					{post.category && (
						<Link
							to={localePath(`/blog/category/${post.category.slug}`)}
							className={cn(
								badgeVariants({ variant: 'soft' }),
								'focus-cosy relative z-10 mb-3 self-start',
							)}
						>
							{getLocalizedField(post.category, 'name', locale)}
						</Link>
					)}

					<h2 className="text-h4 md:text-h3 mb-3 leading-tight font-bold">
						<Link
							to={localePath(`/blog/${post.slug}`)}
							className="group-hover:text-primary transition-colors after:absolute after:inset-0"
						>
							{title}
						</Link>
					</h2>

					{excerpt && (
						<p className="text-body-sm text-muted-foreground mb-4 line-clamp-3 leading-relaxed">
							{excerpt}
						</p>
					)}

					<div className="text-body-xs text-muted-foreground flex items-center gap-4">
						<span>
							{post.author.name || post.author.username}
							{post.publishedAt && (
								<>
									{' · '}
									<time dateTime={new Date(post.publishedAt).toISOString()}>
										{new Date(post.publishedAt).toLocaleDateString(
											lang === 'de'
												? 'de-CH'
												: lang === 'fr'
													? 'fr-CH'
													: 'en-GB',
											{ day: 'numeric', month: 'long', year: 'numeric' },
										)}
									</time>
								</>
							)}
						</span>
					</div>

					{/* Read More: an affordance only, the title link covers the card */}
					<span
						className="text-body-sm text-primary mt-4 inline-flex items-center font-medium"
						aria-hidden="true"
					>
						{t('blog.readMore')}
						<Icon
							name="arrow-right"
							className="ml-1 transition-transform group-hover:translate-x-0.5"
						/>
					</span>
				</div>
			</article>
		</Card>
	)
}

export default function BlogIndex() {
	const { posts, totalPages, currentPage, categories } =
		useLoaderData<typeof loader>()
	const { t } = useTranslation()
	const lang = useLang()
	const locale = lang as SupportedLocale
	const localePath = useLocalizedPath()

	const isFirstPage = currentPage === 1
	const [heroPost, ...remainingPosts] = posts
	const gridPosts = isFirstPage ? remainingPosts : posts
	// No posts on page 1 means an empty blog; past it, a page beyond the last.
	const empty = isFirstPage
		? {
				title: t('blog.empty.title'),
				description: t('blog.empty.description'),
				action: t('notFound.cta'),
				to: '/',
			}
		: {
				title: t('blog.emptyPage.title'),
				description: t('blog.emptyPage.description'),
				action: t('blog.emptyPage.action'),
				to: '/blog',
			}

	return (
		<>
			{/* Hero */}
			<section className="relative overflow-hidden pt-32 pb-24">
				<HeroBackdrop />

				<div className="relative z-10 container text-center">
					<h1 className="text-h2 md:text-h1 mx-auto max-w-3xl font-bold">
						<WordReveal
							text={t('blog.title')}
							wordClassName="text-gradient-brand"
						/>
					</h1>
					<Reveal delay={100}>
						<p className="text-body-md text-muted-foreground md:text-body-lg mx-auto mt-6 max-w-2xl">
							{BLOG_DESCRIPTION}
						</p>
					</Reveal>
					<BlogSearchForm className="mx-auto mt-8 max-w-xl" />

					{/* Category filters */}
					{categories.length > 0 && (
						<nav
							aria-label="Blog categories"
							className="mx-auto mt-8 flex max-w-3xl flex-wrap justify-center gap-2"
						>
							{categories.map((category) => (
								<Link
									key={category.id}
									to={localePath(`/blog/category/${category.slug}`)}
									className={cn(
										glassControlClassName,
										'text-body-sm hover:border-primary/30 hover:bg-primary/5 hover:text-primary rounded-full border px-4 py-1.5 transition-colors',
									)}
								>
									{getLocalizedField(category, 'name', locale)}
								</Link>
							))}
						</nav>
					)}
				</div>
			</section>

			{/* Posts */}
			<div className="container pt-16 pb-16">
				{posts.length === 0 ? (
					<EmptyState
						icon="file-text"
						title={empty.title}
						description={empty.description}
						action={
							<Button asChild variant="outline" size="sm">
								<Link to={localePath(empty.to)}>{empty.action}</Link>
							</Button>
						}
					/>
				) : (
					<>
						{/* Featured Post (page 1 only) */}
						{isFirstPage && heroPost && (
							<FeaturedPost post={heroPost} lang={lang} />
						)}

						{/* Post Grid */}
						{gridPosts.length > 0 && (
							<CardWave
								className={cn(
									'grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3',
									isFirstPage && 'mt-12',
								)}
							>
								{gridPosts.map((post) => (
									<BlogPostCard key={post.id} post={post} lang={lang} />
								))}
							</CardWave>
						)}

						{/* Pagination */}
						{totalPages > 1 && (
							<Pagination
								currentPage={currentPage}
								totalPages={totalPages}
								basePath={localePath('/blog')}
							/>
						)}
					</>
				)}
			</div>
		</>
	)
}
